/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";

export type MetricInfoKey = "pue" | "cue" | "wue" | "cer" | "edc" | "s1s2" | "win";

export interface MetricInfoRange {
	min: number | null;
	max: number | null;
	minInclusive: boolean;
	maxInclusive: boolean;
	assessment: string;
}

export interface MetricInfoDefinition {
	title: string;
	whatIsThis: string;
	scale: "number" | "percentage";
	precision: number;
	ranges: MetricInfoRange[];
}

@Injectable({
	providedIn: "root"
})
export class DataCentersMetricInfoService {
	private readonly _metricInfoByKey: Record<MetricInfoKey, MetricInfoDefinition> = {
		pue: {
			title: "Power Usage Effectiveness (PUE)",
			whatIsThis:
				"Measures how efficiently a data center uses energy. Calculated as total facility energy divided by IT equipment energy.",
			scale: "number",
			precision: 2,
			ranges: [
				{
					min: null,
					max: 1.3,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Exceptional (hyperscaler-level efficiency)"
				},
				{
					min: 1.3,
					max: 1.5,
					minInclusive: true,
					maxInclusive: true,
					assessment: "Good for enterprise data centers"
				},
				{
					min: 1.5,
					max: 1.8,
					minInclusive: false,
					maxInclusive: true,
					assessment: "Acceptable but with headroom to improve"
				},
				{
					min: 1.8,
					max: null,
					minInclusive: false,
					maxInclusive: false,
					assessment:
						"Inefficient; investigate cooling, power distribution, and consolidation strategies"
				}
			]
		},
		cue: {
			title: "Carbon Usage Effectiveness (CUE)",
			whatIsThis:
				"Measures carbon emitted per unit of IT energy consumed. Lower values indicate cleaner power sourcing.",
			scale: "number",
			precision: 2,
			ranges: [
				{
					min: null,
					max: 0.05,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Very low carbon, usually powered by renewables"
				},
				{
					min: 0.05,
					max: 0.15,
					minInclusive: true,
					maxInclusive: true,
					assessment: "Good; reflects moderate grid carbon intensity"
				},
				{
					min: 0.15,
					max: null,
					minInclusive: false,
					maxInclusive: true,
					assessment:
						"High-carbon footprint; consider switching to greener energy sources"
				}
			]
		},
		wue: {
			title: "Water Usage Effectiveness (WUE)",
			whatIsThis:
				"Measures annual water usage per IT energy consumed. Lower values indicate better water stewardship.",
			scale: "number",
			precision: 2,
			ranges: [
				{
					min: null,
					max: 0.2,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Excellent; typically air-cooled or highly optimised"
				},
				{
					min: 0.5,
					max: 1.5,
					minInclusive: true,
					maxInclusive: true,
					assessment: "Typical enterprise baseline"
				},
				{
					min: 1.5,
					max: null,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Water-intensive; investigate free cooling or recirculation"
				}
			]
		},
		cer: {
			title: "Cooling Efficiency Ratio (CER)",
			whatIsThis:
				"Ratio of useful cooling delivered to energy consumed by the cooling system. Higher CER = better efficiency.",
			scale: "number",
			precision: 1,
			ranges: [
				{
					min: 3.0,
					max: null,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Excellent cooling performance"
				},
				{
					min: 2.5,
					max: 3.0,
					minInclusive: true,
					maxInclusive: true,
					assessment: "Standard for enterprise data centres"
				},
				{
					min: null,
					max: 2.5,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Inefficient; check airflow management and CRAC optimisation"
				}
			]
		},
		edc: {
			title: "Energy Consumption (EDC)",
			whatIsThis:
				"Represents total data center energy consumed in the selected period. Tracking trends helps identify efficiency opportunities.",
			scale: "percentage",
			precision: 0,
			ranges: [
				{
					min: null,
					max: -5,
					minInclusive: false,
					maxInclusive: true,
					assessment: "Strong reduction versus baseline"
				},
				{
					min: -5,
					max: 5,
					minInclusive: false,
					maxInclusive: true,
					assessment: "Stable consumption profile"
				},
				{
					min: 5,
					max: 15,
					minInclusive: false,
					maxInclusive: true,
					assessment: "Noticeable growth; monitor demand drivers"
				},
				{
					min: 15,
					max: null,
					minInclusive: false,
					maxInclusive: false,
					assessment: "High increase; assess capacity and efficiency actions"
				}
			]
		},
		s1s2: {
			title: "Total Emissions S1 and S2",
			whatIsThis:
				"Combines direct and purchased-energy emissions. Used to evaluate overall operational carbon footprint.",
			scale: "percentage",
			precision: 0,
			ranges: [
				{
					min: null,
					max: -10,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Strong decarbonization progress"
				},
				{
					min: -10,
					max: 0,
					minInclusive: true,
					maxInclusive: true,
					assessment: "Moderate reduction"
				},
				{
					min: 0,
					max: 10,
					minInclusive: false,
					maxInclusive: true,
					assessment: "Slight increase; track mitigation plans"
				},
				{
					min: 10,
					max: null,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Growing footprint; prioritize reduction initiatives"
				}
			]
		},
		win: {
			title: "Water Usage (WIN)",
			whatIsThis:
				"Represents absolute water withdrawn or consumed for operations in the selected period.",
			scale: "percentage",
			precision: 0,
			ranges: [
				{
					min: null,
					max: -10,
					minInclusive: false,
					maxInclusive: false,
					assessment: "Strong reduction in water demand"
				},
				{
					min: -10,
					max: 0,
					minInclusive: true,
					maxInclusive: true,
					assessment: "Moderate reduction"
				},
				{
					min: 0,
					max: 10,
					minInclusive: false,
					maxInclusive: true,
					assessment: "Slight increase; monitor seasonality and load"
				},
				{
					min: 10,
					max: null,
					minInclusive: false,
					maxInclusive: false,
					assessment: "High increase; investigate cooling and leakage"
				}
			]
		}
	};

	public getMetricInfoByKey(key: MetricInfoKey): MetricInfoDefinition {
		return this._metricInfoByKey[key];
	}

	public getMetricInfoMap(): Readonly<Record<MetricInfoKey, MetricInfoDefinition>> {
		return this._metricInfoByKey;
	}

	public formatRangeLabel(key: MetricInfoKey, range: MetricInfoRange): string {
		const metricInfo = this.getMetricInfoByKey(key);
		const formatValue = (value: number) => this._formatValue(value, metricInfo);
		const minOperator = range.minInclusive ? ">=" : ">";
		const maxOperator = range.maxInclusive ? "<=" : "<";

		if (range.min === null && range.max !== null) {
			return `${maxOperator} ${formatValue(range.max)}`;
		}

		if (range.min !== null && range.max === null) {
			return `${minOperator} ${formatValue(range.min)}`;
		}

		if (range.min !== null && range.max !== null) {
			return `${formatValue(range.min)} - ${formatValue(range.max)}`;
		}

		return "N/A";
	}

	public getRangeForValue(key: MetricInfoKey, value: number): MetricInfoRange | null {
		const metricInfo = this.getMetricInfoByKey(key);
		return metricInfo.ranges.find(range => this.isValueInRange(value, range)) || null;
	}

	public isValueInRange(value: number, range: MetricInfoRange): boolean {
		const minSatisfied =
			range.min === null || (range.minInclusive ? value >= range.min : value > range.min);
		const maxSatisfied =
			range.max === null || (range.maxInclusive ? value <= range.max : value < range.max);

		return minSatisfied && maxSatisfied;
	}

	private _formatValue(value: number, metricInfo: MetricInfoDefinition): string {
		const formatted = value.toFixed(metricInfo.precision);
		const normalized =
			metricInfo.precision === 0 ? String(Math.trunc(value)) : String(Number(formatted));

		if (metricInfo.scale === "percentage") {
			const withSign = value > 0 ? `+${normalized}` : normalized;
			return `${withSign}%`;
		}

		return normalized;
	}
}
